import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import type ApolloClient from 'apollo-client';
import { createAtlAttributionHeader } from '../../utils';
import type { DocumentNode } from 'graphql';

interface UserQueryData {
	user: UserData;
}

export interface UserData {
	accountId: string | undefined;
	name: string;
	picture: string;
}

interface UserQueryVariables {
	accountId: string;
}

export const userQuery: DocumentNode = gql`
	query forge_ui_userQuery($accountId: ID!) {
		user(accountId: $accountId) {
			name
			picture
		}
	}
`;

interface UsersQueryData {
	users: [UserData];
}

interface UsersQueryVariables {
	accountIds: string[];
}

export const usersQuery: DocumentNode = gql`
	query forge_ui_usersQuery($accountIds: [ID!]!) {
		users(accountIds: $accountIds) {
			name
			accountId
			picture
		}
	}
`;

export const useUsers = (
	accountIds: string[],
	client?: ApolloClient<any>,
): {
	[accountId: string]: UserData;
} => {
	const nonEmptyAccountIds = accountIds.filter((id) => !(id === ''));

	const { data } = useQuery<UsersQueryData, UsersQueryVariables>(usersQuery, {
		variables: {
			accountIds: nonEmptyAccountIds,
		},
		client,
		context: {
			headers: {
				'atl-attribution': createAtlAttributionHeader(),
			},
		},
	});

	let users: { [accountId: string]: UserData } = {};

	if (data && data.users) {
		users = data.users.reduce<{
			[accountId: string]: UserData;
		}>((prev, user) => {
			if (user.accountId) {
				prev[user.accountId] = {
					picture: user.picture,
					accountId: user.accountId,
					name: user.name,
				};
			}
			return prev;
		}, {});
	}

	return users;
};

export const useUser = (
	accountId: string,
	client?: ApolloClient<any>,
): {
	name: string;
	picture: string;
} => {
	const { data } = useQuery<UserQueryData, UserQueryVariables>(userQuery, {
		variables: {
			accountId,
		},
		skip: !accountId,
		client,
		context: {
			headers: {
				'atl-attribution': createAtlAttributionHeader(),
			},
		},
	});

	if (data && data.user) {
		const { picture, name } = data.user;
		return {
			name,
			picture,
		};
	}

	return {
		name: '',
		picture: '',
	};
};
